import React from 'react';
import { CheckCircle } from '@mui/icons-material';
import {
  BudgetRequest,
  BudgetRequestStatus,
} from '@models/budget.model';
import { useTranslation } from '@hooks/useTranslation';

interface BudgetHeaderProps {
  activeSubTab: 'received' | 'delegated';
  isDelegatedTask: boolean;
  hasSubDelegation: boolean;
  currentBudgetRequest: BudgetRequest | null;
  isCompletedCancelledBudget: (request: BudgetRequest) => boolean;
}

export const BudgetHeader: React.FC<BudgetHeaderProps> = ({
  activeSubTab,
  isDelegatedTask,
  hasSubDelegation,
  currentBudgetRequest,
  isCompletedCancelledBudget,
}) => {
  const tTaskDetail = useTranslation('taskDetail');

  const getHeaderTitle = () => {
    // Company A (original delegator): Show "Delegated Bid"
    if (!isDelegatedTask && hasSubDelegation) {
      return tTaskDetail.budget.headerDelegatedBid;
    }
    // Company C (final recipient): Show "Received Bid Request"
    if (isDelegatedTask && !hasSubDelegation) {
      return tTaskDetail.budget.headerReceivedBidRequest;
    }
    // Company B (sub-delegator): Show based on active tab
    if (isDelegatedTask && hasSubDelegation) {
      return activeSubTab === 'received'
        ? tTaskDetail.budget.headerReceivedBidRequest
        : tTaskDetail.budget.headerSubDelegatedBid;
    }
    // Fallback
    return tTaskDetail.budget.headerBidRequest;
  };

  return (
    <div className="flex items-center gap-2 pb-4 border-b border-gray-200">
      <CheckCircle className="h-6 w-6 text-blue-500" />
      <h2 className="text-lg font-semibold text-gray-900">
        {getHeaderTitle()}
      </h2>
      {currentBudgetRequest && (
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            isCompletedCancelledBudget(currentBudgetRequest)
              ? 'bg-orange-100 text-orange-800'
              : currentBudgetRequest.status === BudgetRequestStatus.COMPLETED
                ? 'bg-green-100 text-green-800'
                : 'bg-blue-100 text-blue-800'
          }`}>
          {isCompletedCancelledBudget(currentBudgetRequest)
            ? tTaskDetail.budget.statusCompletedCancelled
            : currentBudgetRequest.status}
        </span>
      )}
      {activeSubTab === 'received' && isDelegatedTask && (
        <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
          {tTaskDetail.budget.badgeReceived}
        </span>
      )}
      {activeSubTab === 'delegated' && (
        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
          {tTaskDetail.budget.badgeSent}
        </span>
      )}
    </div>
  );
};